import React, { useEffect, useState } from "react";

import styled from "styled-components/macro";

import { usePaymentMethodProviders } from "Payments/globalPaymentsStateHooks";
import { DateTime } from "luxon";

import FnrButton from "UI/FnrButton3";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import API from "../../API";
import {
  editModalPanels,
  useRecurringGifts,
} from "../../RecurringGiftsContext";
import FundNameTopArea from "../FundNameTopArea";

import AmountSection from "./AmountSection/AmountSection";
import ErrorMsg from "./ErrorMsg";
import MemoSection from "./MemoSection";
import SaveButton from "./SaveButton";
import SelectedPaymentMethod from "./SelectedPaymentMethod";
import ValidationMessage from "./ValidationMessage";

export default function EditPanel() {
  const { state, dispatch } = useRecurringGifts();
  const giftID = state.editModal.currentGift?.ID;
  const { startDate } = state.editModal.giftWithEdits;
  const [fetchStatus, setFetchStatus] = useState(null);
  const { isSubmitting } = state.editModal;
  const { setPaymentMethodProviders } = usePaymentMethodProviders();

  useEffect(
    function fetchGiftDetails() {
      if (!giftID) return;
      // prevent repeatedly calling the endpoint
      if (fetchStatus === "loading" || fetchStatus === "success") return;

      setFetchStatus("loading");

      API.getRecurringGiftDetails(giftID)
        .post()
        .then((res) => {
          setFetchStatus("success");
          dispatch({
            type: "setGiftDetails",
            payload: res,
          });
          setPaymentMethodProviders(res?.paymentMethodProviders);
          // setPaymentMethodProviders([
          //   {
          //     paymentMethodCategory: "credit_card",
          //     paymentProvider: 2,
          //     merchantToken: "Test-Merchant-23f1eba98b9f",
          //     paymentProviderName: "Stax",
          //   },
          //   {
          //     paymentMethodCategory: "bank_account",
          //     paymentProvider: 2,
          //     merchantToken: "Test-Merchant-23f1eba98b9f",
          //     paymentProviderName: "Stax",
          //   },
          // ]);
        })
        .catch((err) => {
          setFetchStatus("error");
        });
    },
    [giftID, dispatch, setPaymentMethodProviders, fetchStatus]
  );

  useEffect(() => {
    let date = DateTime.fromISO(startDate).toLocaleString(DateTime.DATE_MED);
    let today = DateTime.now().toLocaleString(DateTime.DATE_MED);
    if ((!startDate && typeof startDate !== "undefined") || date === today)
      dispatch({ type: "setEditPanelValidationError", payload: "today" });
  }, [startDate, dispatch]);

  const resetInitialMemo = () => {
    /**
     * This is a hacky way to reset the current memo
     * Tiptap is a mostly an uncontrolled component. We pass in functions to capture the content changes.
     * But I've set the editor in MemoForm to change the content to the initialContent value any time it changes.
     * There is no initialContent on the Fund It page, so it never triggers there.
     * When we cancel editing here, the initial content doesn't change because it is based on currentGift.memo, but we want tiptap to revert to the initial content
     * We clear the value, then set it back to what it was originally.
     * We have to use a setTimeout because React aggregates the dispatches if we run them one right after the other.
     * This lets us reset it without re-architecting how the Memo component is put together.
     */
    let initialMemo = state.editModal?.currentGift?.memo;
    dispatch({ type: "setCurrentMemo", payload: "" });

    setTimeout(() => {
      dispatch({ type: "setCurrentMemo", payload: initialMemo });
    }, 100);
  };

  const handleCancelClick = () => {
    dispatch({
      type: "setCurrentPanel",
      payload: editModalPanels.initial,
    });
    dispatch({ type: "resetGiftWithEdits" });
    resetInitialMemo();
  };

  if (fetchStatus === "error")
    return <ErrorMsg handleCancelClick={handleCancelClick} />;

  return (
    <Wrapper>
      <FundNameTopArea />
      <FnrVerticalGap gap={20}>
        <Section>
          <AmountSection />
        </Section>

        <Section>
          <SelectedPaymentMethod />
        </Section>

        <MemoSection />

        <ValidationMessage />

        <Buttons>
          <FnrButton onClick={handleCancelClick} disabled={isSubmitting}>
            Cancel
          </FnrButton>

          <SaveButton />
        </Buttons>
      </FnrVerticalGap>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: 20px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const Section = styled.div`
  box-shadow: 0 8px 24px 0 #eeeeeeff, 0 4px 8px 0 #42474c0f, 0 0 1px 0 #42474c52;
  padding: 14px;
  border-radius: 14px;
  color: #474747;
`;
